"use client";
import { useEffect, useState, memo, useRef, useMemo } from "react";
import { EditorialListingsProps } from "./types";
import propsToAttributes from "@/utils/propsToAttributes";
import { StackedELSkeletonLoader } from "./loaders";
import useTranslations from "@/i18n/useTranslations";

const DEFAULT_STYLE = `
  --component-border-size:0;
  --component-border-radius: 12px;
  --component-background: #00262B;
  --title-text-color: white;
  --subtitle-text-color: white;
  --sonic-tooltip-border-color: white;
  --sonic-tooltip-label-color:white;
  --cta-card-button-color: #D74000;
  --cta-card-button-border-radius: 10px;
  --header-text-align: left;
`;

declare global {
  interface Window {
    HE_UCL: {
      loadComponent: (component: string) => void;
      queryComponents: () => string[];
    };
  }
}

const AffiliateEL = memo((props: EditorialListingsProps) => {
  const affiliateAttributes = propsToAttributes(props);
  return (
    <div
      dangerouslySetInnerHTML={{
        __html: `<affiliate-el${affiliateAttributes}>`,
      }}
    />
  );
});
AffiliateEL.displayName = "AffiliateEL";

const AffiliateELWrapper = (props: EditorialListingsProps) => {
  const t = useTranslations("EditorialListings");
  const defaultProps: EditorialListingsProps = useMemo(
    () => ({
      layout: "stacked",
      showRedirectNotice: "true",
      limit: "4",
      buttonText: t("visitSite"),
      advertisementText: t("advertisement"),
      showDisclaimerIcon: "true",
      degree: "general",
      category: "general",
      subject: "general",
      title: t("acceptingStudentsTitle"),
      subtitle: t("findProgramSubtitle"),
      moreButtonText: t("viewMorePrograms"),
      showMoreResults: "false",
      hideViewMoreButton: "true",
      swapElTitle: "false",
      ctaExternalRedirectIcon: "true",
      style: DEFAULT_STYLE,
      adDisclaimerText: t("adDisclaimer"),
      adDisclaimerPlacement: "top",
    }),
    [t],
  );
  const mergedProps: EditorialListingsProps = { ...defaultProps, ...props };
  const [isLoaded, setIsLoaded] = useState(false);
  const affiliateRef = useRef<HTMLDivElement>(null);
  const attemptCountRef = useRef(0);
  const MAX_ATTEMPTS = 10;

  const forceLoadSonicComponent = () => {
    const componentToLoad = "affiliate-el";
    const currComponents = window.HE_UCL?.queryComponents();
    const componentLoaded = currComponents.find((el) => {
      el === componentToLoad;
    });
    if (!componentLoaded) window?.HE_UCL?.loadComponent(componentToLoad);
  };

  useEffect(() => {
    const checkComponent = () => {
      attemptCountRef.current += 1;
      forceLoadSonicComponent();
      // Get the affiliate element from the ref's DOM node
      const affiliateElement =
        affiliateRef.current?.querySelector("affiliate-el");

      if (affiliateElement?.shadowRoot) {
        setIsLoaded(true);
        return true;
      }

      if (attemptCountRef.current >= MAX_ATTEMPTS) {
        return true;
      }

      return false;
    };

    const id = setInterval(() => {
      if (checkComponent()) {
        clearInterval(id);
      }
    }, 1000);

    return () => clearInterval(id);
  }, []);

  return (
    <div className="mt-10" ref={affiliateRef}>
      {!isLoaded && (
        <StackedELSkeletonLoader limit={parseInt(mergedProps.limit ?? "3")} />
      )}
      <div className={`${!isLoaded ? "hidden" : ""}`}>
        <AffiliateEL {...mergedProps} />
      </div>
    </div>
  );
};

export default AffiliateELWrapper;
